<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useMyOrganizations } from '@ilteducation/my-account';
import type { SlimOrganization } from '@ilteducation/my-account-types';
import useLocale from './helpers/use-locale';

type Emits = {
  (e: 'update:modelValue', organization: SlimOrganization | undefined): void;
  (e: 'isLoading', isLoading: boolean): void;
};
const emit = defineEmits<Emits>();

const modelValue = ref<undefined | SlimOrganization>(undefined);

const { t } = useLocale();
const { organizations, loadMyOrganizations, isLoading: isLoadingOrganizations } = useMyOrganizations(false);

const items = computed(() => organizations.value.data);

onMounted(async () => {
  await loadMyOrganizations();
  modelValue.value = organizations.value.data[0];
  emit('update:modelValue', modelValue.value);
});

watch(isLoadingOrganizations, (newValue: boolean) => {
  emit('isLoading', newValue);
});
</script>

<template>
  <v-select
    v-model="modelValue"
    :label="t('components.organisation')"
    :aria-label="t('components.organisation')"
    bg-color="surface-container-high"
    variant="solo"
    v-bind="{ ...$attrs }"
    density="comfortable"
    :items="items"
    value-property="name"
    item-title="name"
    item-value="uid"
    :return-object="true"
    :hide-details="true"
    :single-line="true"
    :loading="isLoadingOrganizations"
    :disabled="!modelValue"
    flat
    @update:modelValue="emit('update:modelValue', $event)"
    aria-haspopup="menu"
    role="button"
  >
    <template #prepend-inner>
      <slot name="prepend-inner">
        <div class="bg-black rounded px-3 py-2 ml-n3">
          <v-icon icon="mdi-home-city" color="surface-container-high"></v-icon>
        </div>
      </slot>
    </template>
  </v-select>
</template>

<style scoped lang="scss"></style>
